/**
 * DeliveryOverview - Landing page for the Delivery & Fulfillment module
 * Shows stats, recent shipments, and quick actions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../lib/api';
import {
  Package, Truck, Tag, Clock, CheckCircle2, Plus, Upload,
  ArrowRight, TrendingUp, AlertCircle, Map
} from 'lucide-react';
import { DELIVERY_STATUS_COLORS } from '../manifest';
import DeliveryImportModal from '../components/DeliveryImportModal';

export default function DeliveryOverview() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/delivery/shipments');
      setShipments(data || []);
    } catch (err) {
      console.error('Error fetching delivery shipments:', err);
      setShipments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, []);

  const stats = useMemo(() => {
    const active = shipments.filter(s => !s.archived);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return {
      total:         active.length,
      imported:      active.filter(s => s.status === 'Imported').length,
      labelsPrinted: active.filter(s => s.status === 'Labels Printed').length,
      inTransit:     active.filter(s => s.status === 'In Transit').length,
      delivered:     active.filter(s => s.status === 'Delivered').length,
      pendingPrint:  active.filter(s => s.status === 'Imported').length,
      deliveredToday: active.filter(s => {
        if (s.status !== 'Delivered' || !s.updated_at) return false;
        return new Date(s.updated_at) >= today;
      }).length,
    };
  }, [shipments]);

  const recentShipments = useMemo(() => {
    return [...shipments]
      .filter(s => !s.archived)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 6);
  }, [shipments]);

  const card = `rounded-xl border p-5 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`;
  const label = `text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const value = `text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`;

  return (
    <div className={`flex flex-col h-full overflow-y-auto p-6 gap-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Delivery & Fulfillment
          </h1>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage shipments, generate PLX labels, and track deliveries
          </p>
        </div>
        <button
          onClick={() => setShowImport(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors text-sm"
        >
          <Upload size={16} />
          Import Shipment
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className={card}>
          <div className={label}>Total Shipments</div>
          <div className={value}>{loading ? '—' : stats.total}</div>
          <div className="flex items-center gap-1 mt-2">
            <Package size={14} className="text-violet-400" />
            <span className="text-xs text-violet-400">All active</span>
          </div>
        </div>

        <div className={card}>
          <div className={label}>Imported</div>
          <div className={`${value} text-slate-400`}>{loading ? '—' : stats.imported}</div>
          <div className="flex items-center gap-1 mt-2">
            <Clock size={14} className="text-slate-400" />
            <span className="text-xs text-slate-400">Awaiting labels</span>
          </div>
        </div>

        <div className={card}>
          <div className={label}>Labels Printed</div>
          <div className={`${value} text-violet-400`}>{loading ? '—' : stats.labelsPrinted}</div>
          <div className="flex items-center gap-1 mt-2">
            <Tag size={14} className="text-violet-400" />
            <span className="text-xs text-violet-400">Ready to ship</span>
          </div>
        </div>

        <div className={card}>
          <div className={label}>In Transit</div>
          <div className={`${value} text-amber-400`}>{loading ? '—' : stats.inTransit}</div>
          <div className="flex items-center gap-1 mt-2">
            <Truck size={14} className="text-amber-400" />
            <span className="text-xs text-amber-400">On the road</span>
          </div>
        </div>

        <div className={card}>
          <div className={label}>Delivered</div>
          <div className={`${value} text-green-400`}>{loading ? '—' : stats.delivered}</div>
          <div className="flex items-center gap-1 mt-2">
            <CheckCircle2 size={14} className="text-green-400" />
            <span className="text-xs text-green-400">{stats.deliveredToday} today</span>
          </div>
        </div>
      </div>

      {/* In Transit Alert → Live Map */}
      {stats.inTransit > 0 && (
        <div
          className="flex items-center justify-between px-4 py-3 rounded-lg border border-amber-500/40 bg-amber-500/10 cursor-pointer hover:bg-amber-500/20 transition-colors"
          onClick={() => navigate('/delivery/tracking')}
        >
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className={`text-sm font-medium ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
              {stats.inTransit} shipment{stats.inTransit !== 1 ? 's' : ''} currently in transit
            </span>
          </div>
          <div className="flex items-center gap-1 text-amber-400 text-sm font-medium">
            View Live Map <ArrowRight size={14} />
          </div>
        </div>
      )}

      {/* Pending Print Alert */}
      {stats.pendingPrint > 0 && (
        <div
          className="flex items-center justify-between px-4 py-3 rounded-lg border border-violet-500/40 bg-violet-500/10 cursor-pointer hover:bg-violet-500/20 transition-colors"
          onClick={() => navigate('/delivery/labels')}
        >
          <div className="flex items-center gap-3">
            <AlertCircle size={18} className="text-violet-400" />
            <span className={`text-sm font-medium ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
              {stats.pendingPrint} shipment{stats.pendingPrint !== 1 ? 's' : ''} waiting for labels to be printed
            </span>
          </div>
          <div className="flex items-center gap-1 text-violet-400 text-sm font-medium">
            Go to Labels <ArrowRight size={14} />
          </div>
        </div>
      )}

      {/* Recent Shipments + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Recent Shipments */}
        <div className={`lg:col-span-2 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent Shipments</h2>
            <button
              onClick={() => navigate('/delivery/shipments')}
              className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 transition-colors font-medium"
            >
              View all <ArrowRight size={12} />
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : recentShipments.length === 0 ? (
            <div className="p-8 text-center">
              <Package size={32} className={`mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No shipments yet</p>
              <button
                onClick={() => setShowImport(true)}
                className="mt-3 text-sm text-violet-400 hover:text-violet-300 font-medium"
              >
                Import your first shipment →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {recentShipments.map(s => {
                const colors = DELIVERY_STATUS_COLORS[s.status] || DELIVERY_STATUS_COLORS['Imported'];
                return (
                  <div
                    key={s.id}
                    className={`flex items-center gap-4 px-5 py-3.5 cursor-pointer transition-colors ${isDark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}`}
                    onClick={() => navigate('/delivery/shipments')}
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {s.delivery_no || s.shipment_no || s.id.slice(0, 8)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${colors.bg} ${colors.border} ${colors.text}`}>
                          {s.status}
                        </span>
                      </div>
                      <div className={`text-xs mt-0.5 truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {s.customer_name && <span>{s.customer_name}</span>}
                        {s.stop_no != null && <span> · Stop #{s.stop_no}</span>}
                        {s.box_quantity && <span> · Qty: {s.box_quantity}</span>}
                      </div>
                    </div>
                    <div className={`text-xs flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Quick Actions</h2>
          </div>
          <div className="p-4 space-y-3">
            <button
              onClick={() => setShowImport(true)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                isDark
                  ? 'border-gray-700 hover:bg-gray-700/50 text-gray-200'
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                <Upload size={16} className="text-violet-400" />
              </div>
              <div>
                <div className="text-sm font-medium">Import Shipment</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Upload CSV or JSON file</div>
              </div>
            </button>

            <button
              onClick={() => navigate('/delivery/labels')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                isDark
                  ? 'border-gray-700 hover:bg-gray-700/50 text-gray-200'
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                <Tag size={16} className="text-violet-400" />
              </div>
              <div>
                <div className="text-sm font-medium">Print Labels</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Generate PLX delivery labels</div>
              </div>
            </button>

            <button
              onClick={() => navigate('/delivery/shipments')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                isDark
                  ? 'border-gray-700 hover:bg-gray-700/50 text-gray-200'
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                <Truck size={16} className="text-violet-400" />
              </div>
              <div>
                <div className="text-sm font-medium">View Shipments</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Kanban & list view</div>
              </div>
            </button>

            <button
              onClick={() => navigate('/delivery/tracking')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                isDark
                  ? 'border-gray-700 hover:bg-gray-700/50 text-gray-200'
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-amber-600/20 flex items-center justify-center flex-shrink-0">
                <Map size={16} className="text-amber-400" />
              </div>
              <div>
                <div className="text-sm font-medium">Live Tracking Map</div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>View all active deliveries</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImport && (
        <DeliveryImportModal
          isDark={isDark}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchShipments(); }}
        />
      )}
    </div>
  );
}
